import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit3, Save, X, Upload, HeartPulse, Layers, BookOpen, GraduationCap, FileText, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API, useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const STATUS_OPTIONS = ['draft', 'incomplete', 'published', 'coming_soon', 'archived'];
const RESOURCE_TYPES = ['notes', 'lesson_plan', 'worksheet', 'question_bank', 'assessment', 'book', 'activity', 'pdf', 'image', 'video', 'other'];

export default function AdminCMSPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('resources');

  if (!user) return <div className="p-6 text-center text-sm">Please <Link className="text-emerald-600 font-bold" to="/">login</Link> as admin.</div>;

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> Home
      </Link>
      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #7c3aed 60%, #0891b2 100%)' }}>
        <Layers size={26} />
        <h1 className="text-xl font-extrabold mt-2">🎛️ Admin CMS Dashboard</h1>
        <p className="text-xs text-white/90 mt-1">Class → Subject → Topic → Resource · Complete content management</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar text-xs">
        {[
          { k: 'resources', label: '📄 Resources', i: FileText },
          { k: 'topics', label: '🎯 Topics', i: Layers },
          { k: 'subjects', label: '📖 Subjects', i: BookOpen },
          { k: 'classes', label: '🎓 Classes', i: GraduationCap },
          { k: 'health', label: '💚 Health', i: HeartPulse },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`cms-tab-${t.k}`}
            className={`shrink-0 px-3 py-2 rounded-full font-bold whitespace-nowrap ${tab === t.k ? 'bg-red-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'resources' && <ResourcesTab />}
      {tab === 'topics' && <TopicsTab />}
      {tab === 'subjects' && <SubjectsTab />}
      {tab === 'classes' && <ClassesTab />}
      {tab === 'health' && <HealthTab />}
    </div>
  );
}

/* -------------------- CLASSES -------------------- */
function ClassesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name_odia: '', name_en: '', order: 0, status: 'published' });
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/admin/cms/classes`, { withCredentials: true }).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editing) await axios.put(`${API}/admin/cms/classes/${editing}`, form, { withCredentials: true });
      else await axios.post(`${API}/admin/cms/classes`, form, { withCredentials: true });
      toast({ title: editing ? 'Updated ✅' : 'Added ✅' });
      setForm({ name_odia: '', name_en: '', order: 0, status: 'published' }); setEditing(null); load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Error' }); }
  };

  return (
    <>
      <Card className="rounded-2xl p-4 border-gray-200 space-y-2">
        <div className="font-bold text-sm">{editing ? 'Edit Class' : '+ Add Class'}</div>
        <Input placeholder="Name (Odia)" value={form.name_odia} onChange={e => setForm({...form, name_odia: e.target.value})} data-testid="class-name-odia" className="h-10 text-sm"/>
        <Input placeholder="Name (English)" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} data-testid="class-name-en" className="h-10 text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="h-10 rounded-lg border border-gray-300 px-2 text-sm">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <Button onClick={submit} data-testid="class-save" className="flex-1 bg-red-600 hover:bg-red-700 text-white"><Save size={14} className="mr-1"/> Save</Button>
          {editing && <Button onClick={() => { setEditing(null); setForm({ name_odia: '', name_en: '', order: 0, status: 'published' }); }} variant="outline">Cancel</Button>}
        </div>
      </Card>
      <div className="space-y-1.5">
        {items.map(c => (
          <div key={c.id} className="p-3 rounded-xl bg-white border border-gray-200 flex items-center gap-2" data-testid={`class-item-${c.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{c.name_odia} · {c.name_en}</div>
              <div className="text-[10px] text-gray-500">slug: {c.slug} · order: {c.order} · status: {c.status}</div>
            </div>
            <button onClick={() => { setEditing(c.id); setForm({ name_odia: c.name_odia, name_en: c.name_en, order: c.order, status: c.status }); }} className="p-1.5 text-blue-600"><Edit3 size={14}/></button>
            <button onClick={async () => { if (window.confirm('Archive?')) { await axios.delete(`${API}/admin/cms/classes/${c.id}`, { withCredentials: true }); load(); }}} className="p-1.5 text-red-600"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------- SUBJECTS -------------------- */
function SubjectsTab() {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ name_odia: '', name_en: '', class_ids: [], order: 0, status: 'published' });
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API}/admin/cms/subjects`, { withCredentials: true }).then(r => setItems(r.data));
  useEffect(() => { load(); axios.get(`${API}/admin/cms/classes`, { withCredentials: true }).then(r => setClasses(r.data)); }, []);

  const submit = async () => {
    try {
      if (editing) await axios.put(`${API}/admin/cms/subjects/${editing}`, form, { withCredentials: true });
      else await axios.post(`${API}/admin/cms/subjects`, form, { withCredentials: true });
      toast({ title: 'Saved ✅' });
      setForm({ name_odia: '', name_en: '', class_ids: [], order: 0, status: 'published' }); setEditing(null); load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Error' }); }
  };

  return (
    <>
      <Card className="rounded-2xl p-4 border-gray-200 space-y-2">
        <div className="font-bold text-sm">{editing ? 'Edit Subject' : '+ Add Subject'}</div>
        <Input placeholder="Name (Odia)" value={form.name_odia} onChange={e => setForm({...form, name_odia: e.target.value})} data-testid="subject-name-odia" className="h-10 text-sm"/>
        <Input placeholder="Name (English)" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} data-testid="subject-name-en" className="h-10 text-sm"/>
        <div>
          <label className="text-[11px] font-bold">Assign to Classes:</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {classes.map(c => (
              <button key={c.id} type="button" onClick={() => setForm(f => ({ ...f, class_ids: f.class_ids.includes(c.id) ? f.class_ids.filter(x => x !== c.id) : [...f.class_ids, c.id] }))}
                className={`text-[10px] px-2 py-1 rounded-full font-bold ${form.class_ids.includes(c.id) ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {c.name_odia}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="h-10 rounded-lg border border-gray-300 px-2 text-sm">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <Button onClick={submit} data-testid="subject-save" className="flex-1 bg-red-600 hover:bg-red-700 text-white"><Save size={14} className="mr-1"/> Save</Button>
          {editing && <Button onClick={() => { setEditing(null); setForm({ name_odia: '', name_en: '', class_ids: [], order: 0, status: 'published' }); }} variant="outline">Cancel</Button>}
        </div>
      </Card>
      <div className="space-y-1.5">
        {items.map(s => (
          <div key={s.id} className="p-3 rounded-xl bg-white border border-gray-200 flex items-center gap-2" data-testid={`subject-item-${s.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{s.name_odia} · {s.name_en}</div>
              <div className="text-[10px] text-gray-500">slug: {s.slug} · {(s.class_ids || []).length} classes · {s.status}</div>
            </div>
            <button onClick={() => { setEditing(s.id); setForm({ name_odia: s.name_odia, name_en: s.name_en, class_ids: s.class_ids || [], order: s.order || 0, status: s.status }); }} className="p-1.5 text-blue-600"><Edit3 size={14}/></button>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------- TOPICS -------------------- */
function TopicsTab() {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filter, setFilter] = useState({ class_id: '', subject_id: '' });
  const [form, setForm] = useState({ class_id: '', subject_id: '', name_odia: '', name_en: '', chapter_number: 0, order: 0, status: 'published', description_odia: '' });

  const load = React.useCallback(() => {
    const p = new URLSearchParams();
    if (filter.class_id) p.append('class_id', filter.class_id);
    if (filter.subject_id) p.append('subject_id', filter.subject_id);
    axios.get(`${API}/admin/cms/topics?${p}`, { withCredentials: true }).then(r => setItems(r.data));
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/cms/classes`, { withCredentials: true }).then(r => setClasses(r.data));
    axios.get(`${API}/admin/cms/subjects`, { withCredentials: true }).then(r => setSubjects(r.data));
  }, []);

  const submit = async () => {
    try {
      await axios.post(`${API}/admin/cms/topics`, form, { withCredentials: true });
      toast({ title: 'Topic added ✅' });
      setForm({ ...form, name_odia: '', name_en: '', chapter_number: form.chapter_number + 1, order: form.order + 1, description_odia: '' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Error' }); }
  };

  const filteredSubjects = filter.class_id ? subjects.filter(s => (s.class_ids || []).includes(filter.class_id)) : subjects;
  const formSubjects = form.class_id ? subjects.filter(s => (s.class_ids || []).includes(form.class_id)) : subjects;

  return (
    <>
      <Card className="rounded-2xl p-4 border-gray-200 space-y-2">
        <div className="font-bold text-sm">+ Add Topic / ପ୍ରସଙ୍ଗ</div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.class_id} onChange={e => setForm({...form, class_id: e.target.value, subject_id: ''})} data-testid="topic-class" className="h-10 rounded-lg border px-2 text-sm">
            <option value="">Select Class...</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name_odia}</option>)}
          </select>
          <select value={form.subject_id} onChange={e => setForm({...form, subject_id: e.target.value})} data-testid="topic-subject" className="h-10 rounded-lg border px-2 text-sm">
            <option value="">Select Subject...</option>
            {formSubjects.map(s => <option key={s.id} value={s.id}>{s.name_odia}</option>)}
          </select>
        </div>
        <Input placeholder="Topic (Odia)" value={form.name_odia} onChange={e => setForm({...form, name_odia: e.target.value})} data-testid="topic-name-odia" className="h-10 text-sm"/>
        <Input placeholder="Topic (English)" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} data-testid="topic-name-en" className="h-10 text-sm"/>
        <div className="grid grid-cols-3 gap-2">
          <Input type="number" placeholder="Chapter#" value={form.chapter_number} onChange={e => setForm({...form, chapter_number: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
          <Input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="h-10 rounded-lg border px-2 text-sm">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Input placeholder="Description (Odia)" value={form.description_odia} onChange={e => setForm({...form, description_odia: e.target.value})} className="h-10 text-sm"/>
        <Button onClick={submit} disabled={!form.class_id || !form.subject_id || !form.name_odia} data-testid="topic-save" className="w-full bg-red-600 hover:bg-red-700 text-white"><Plus size={14} className="mr-1"/> Add Topic</Button>
      </Card>

      {/* Filter */}
      <div className="flex gap-2">
        <select value={filter.class_id} onChange={e => setFilter({...filter, class_id: e.target.value, subject_id: ''})} className="flex-1 h-10 rounded-lg border px-2 text-sm">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name_odia}</option>)}
        </select>
        <select value={filter.subject_id} onChange={e => setFilter({...filter, subject_id: e.target.value})} className="flex-1 h-10 rounded-lg border px-2 text-sm">
          <option value="">All Subjects</option>
          {filteredSubjects.map(s => <option key={s.id} value={s.id}>{s.name_odia}</option>)}
        </select>
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-center py-4 text-sm text-gray-500">No topics found</div>}
        {items.map(t => (
          <div key={t.id} className="p-3 rounded-xl bg-white border border-gray-200 flex items-center gap-2" data-testid={`topic-item-${t.slug}`}>
            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">{t.chapter_number || '•'}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{t.name_odia}</div>
              <div className="text-[10px] text-gray-500 truncate">{t.name_en} · order {t.order} · {t.status}</div>
            </div>
            <button onClick={async () => { if (window.confirm('Archive?')) { await axios.delete(`${API}/admin/cms/topics/${t.id}`, { withCredentials: true }); load(); }}} className="p-1.5 text-red-600"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------- RESOURCES -------------------- */
function ResourcesTab() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    class_id: '', subject_id: '', topic_id: '', resource_type: 'notes',
    title: '', title_odia: '', description: '', description_odia: '', content: '',
    tags: '', order: 0, status: 'draft', completion_percent: 100, file_url_external: '',
    permission_status: 'own',
  });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/admin/cms/classes`, { withCredentials: true }).then(r => setClasses(r.data));
    axios.get(`${API}/admin/cms/subjects`, { withCredentials: true }).then(r => setSubjects(r.data));
  }, []);

  const load = React.useCallback(() => {
    const p = filter === 'all' ? '' : `?status=${filter}`;
    axios.get(`${API}/admin/cms/resources${p}`, { withCredentials: true }).then(r => setItems(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.class_id && form.subject_id) {
      axios.get(`${API}/admin/cms/topics?class_id=${form.class_id}&subject_id=${form.subject_id}`, { withCredentials: true }).then(r => setTopics(r.data));
    } else setTopics([]);
  }, [form.class_id, form.subject_id]);

  const formSubjects = form.class_id ? subjects.filter(s => (s.class_ids || []).includes(form.class_id)) : subjects;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (file) fd.append('file', file);
      await axios.post(`${API}/admin/cms/resources`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: `Saved as ${form.status} ✅` });
      setFile(null);
      setForm(f => ({ ...f, title: '', title_odia: '', description: '', description_odia: '', content: '', tags: '' }));
      load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const publish = async (id) => {
    try {
      await axios.put(`${API}/admin/cms/resources/${id}`, { status: 'published' }, { withCredentials: true });
      toast({ title: 'Published ✅' }); load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Cannot publish' }); }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-2">
        <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50 space-y-2">
          <div className="font-bold text-sm text-emerald-900">➕ Add Content · Class → Subject → Topic → Resource</div>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.class_id} onChange={e => setForm({...form, class_id: e.target.value, subject_id: '', topic_id: ''})} data-testid="res-class" required className="h-10 rounded-lg border px-2 text-sm">
              <option value="">Class...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name_odia}</option>)}
            </select>
            <select value={form.subject_id} onChange={e => setForm({...form, subject_id: e.target.value, topic_id: ''})} data-testid="res-subject" required disabled={!form.class_id} className="h-10 rounded-lg border px-2 text-sm">
              <option value="">Subject...</option>
              {formSubjects.map(s => <option key={s.id} value={s.id}>{s.name_odia}</option>)}
            </select>
            <select value={form.topic_id} onChange={e => setForm({...form, topic_id: e.target.value})} data-testid="res-topic" required disabled={!form.subject_id} className="h-10 rounded-lg border px-2 text-sm col-span-2">
              <option value="">Topic / ପ୍ରସଙ୍ଗ...</option>
              {topics.map(t => <option key={t.id} value={t.id}>{t.name_odia}</option>)}
            </select>
            <select value={form.resource_type} onChange={e => setForm({...form, resource_type: e.target.value})} data-testid="res-type" required className="h-10 rounded-lg border px-2 text-sm col-span-2">
              {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <Input placeholder="Title (English) *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="res-title" required className="h-10 text-sm"/>
          <Input placeholder="Title (Odia) *" value={form.title_odia} onChange={e => setForm({...form, title_odia: e.target.value})} data-testid="res-title-odia" className="h-10 text-sm"/>
          <Input placeholder="Description (Odia)" value={form.description_odia} onChange={e => setForm({...form, description_odia: e.target.value})} className="h-10 text-sm"/>
          <textarea placeholder="Content (rich text — optional)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="w-full min-h-[80px] rounded-lg border px-2 py-1 text-sm"/>
          <div>
            <label className="text-[11px] font-bold">PDF / File (optional):</label>
            <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="res-file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.mp3,.mp4,.webm,.zip"
              className="w-full text-xs mt-1"/>
            {file && <p className="text-[10px] text-gray-600 mt-1">{file.name} · {(file.size/1024/1024).toFixed(2)}MB</p>}
          </div>
          <Input placeholder="External file URL (optional, if not uploading)" value={form.file_url_external} onChange={e => setForm({...form, file_url_external: e.target.value})} className="h-10 text-sm"/>
          <Input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="h-10 text-sm"/>
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
            <Input type="number" placeholder="%" min="0" max="100" value={form.completion_percent} onChange={e => setForm({...form, completion_percent: parseInt(e.target.value || 0)})} className="h-10 text-sm"/>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="res-status" className="h-10 rounded-lg border px-2 text-sm">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button type="submit" disabled={busy} data-testid="res-save" className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-extrabold">
            {busy ? 'Saving...' : (form.status === 'published' ? '🚀 Publish' : '💾 Save Draft')}
          </Button>
        </Card>
      </form>

      <div className="flex gap-1.5 flex-wrap text-xs">
        {['all', ...STATUS_OPTIONS].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-2.5 py-1 rounded-full font-bold ${filter === s ? 'bg-red-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-center py-6 text-sm text-gray-500">No resources yet</div>}
        {items.map(r => (
          <div key={r.id} className="p-3 rounded-xl bg-white border border-gray-200" data-testid={`res-item-${r.id}`}>
            <div className="flex items-start gap-2">
              <FileText size={16} className="text-emerald-600 shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{r.title_odia || r.title}</div>
                <div className="text-[10px] text-gray-500">{r.resource_type} · {r.status} · order {r.order} · {r.completion_percent}%</div>
                <div className="text-[10px] text-gray-400 mt-1">👁 {r.views || 0} · ⬇ {r.downloads || 0}</div>
              </div>
              <div className="flex flex-col gap-1">
                {r.status !== 'published' && <button onClick={() => publish(r.id)} data-testid={`res-publish-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white font-bold">Publish</button>}
                <button onClick={async () => { if (window.confirm('Archive?')) { await axios.delete(`${API}/admin/cms/resources/${r.id}`, { withCredentials: true }); load(); }}} className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">Archive</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------- HEALTH -------------------- */
function HealthTab() {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/cms/content-health`, { withCredentials: true }).then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-center py-6 text-sm text-gray-500">Loading...</div>;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 text-center">
        {Object.entries(data.totals).map(([k, v]) => (
          <div key={k} className="p-3 rounded-xl bg-white border border-gray-200">
            <div className="text-2xl font-extrabold text-red-600">{v}</div>
            <div className="text-[10px] text-gray-500 capitalize">{k.replace('_', ' ')}</div>
          </div>
        ))}
      </div>
      <Card className="rounded-2xl p-3 border-gray-200">
        <div className="font-bold text-sm mb-2">⚠️ Broken / Incomplete ({data.broken_or_incomplete.length})</div>
        {data.broken_or_incomplete.length === 0 ? <div className="text-xs text-emerald-600">All good ✅</div> :
          <div className="space-y-1">
            {data.broken_or_incomplete.slice(0, 20).map(x => (
              <div key={x.id} className="text-[11px] py-1 border-b border-gray-100">
                <span className="font-bold">{x.title || '(no title)'}</span> · <span className="text-red-600">{x.issues.join(', ')}</span>
              </div>
            ))}
          </div>}
      </Card>
      <Card className="rounded-2xl p-3 border-gray-200">
        <div className="font-bold text-sm mb-2">🏆 Top Downloads</div>
        {data.top_downloads.map(t => (
          <div key={t.id} className="text-[11px] py-1 flex justify-between">
            <span className="truncate flex-1">{t.title_odia || t.title}</span>
            <span className="font-bold text-emerald-600 ml-2">⬇ {t.downloads || 0}</span>
          </div>
        ))}
      </Card>
      <Button onClick={load} className="w-full bg-gray-100 text-gray-700"><RefreshCw size={14} className="mr-1"/> Refresh</Button>
    </>
  );
}
